import type { DashboardSettingDto } from '../api/dashboardSettings.api';
import type { DashboardSettingFieldConfig, DashboardSettingKind } from '../dashboardSettingNames';
import { getFieldConfig } from '../dashboardSettingNames';
import {
  parseDecimalSetting,
  parseIntegerSetting,
  parsePercentSetting,
  parseUsdSetting,
} from '../utils/dashboardSettingParsers';

export type ResolvedDashboardSettingSource = 'settings' | 'default';

export type ResolvedDashboardSetting = {
  name: string;
  value: number;
  defaultValue: number;
  source: ResolvedDashboardSettingSource;
  displayValue: string;
};

function parseDefaultNumber(config: DashboardSettingFieldConfig): number {
  switch (config.kind) {
    case 'percent':
    case 'integer':
    case 'usd':
      return parseIntegerSetting(config.defaultValue, 0);
    case 'decimal':
      return parseDecimalSetting(config.defaultValue, 0);
    default:
      return parseIntegerSetting(config.defaultValue, 0);
  }
}

function isParsedValid(kind: DashboardSettingKind, raw: string, parsed: number): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (!Number.isFinite(parsed)) return false;
  switch (kind) {
    case 'percent':
    case 'integer':
      return Number.isInteger(parsed);
    case 'usd':
      return Number.isInteger(parsed) && parsed >= 0;
    case 'decimal':
      return parsed > 0;
    default:
      return true;
  }
}

function parseRawValue(config: DashboardSettingFieldConfig, raw: string | undefined | null): number {
  const fallback = parseDefaultNumber(config);
  switch (config.kind) {
    case 'percent':
      return parsePercentSetting(raw, fallback);
    case 'integer':
      return parseIntegerSetting(raw, fallback);
    case 'usd':
      return parseUsdSetting(raw, fallback);
    case 'decimal':
      return parseDecimalSetting(raw, fallback);
    default:
      return fallback;
  }
}

export function formatSettingDisplayValue(config: DashboardSettingFieldConfig, value: number): string {
  switch (config.kind) {
    case 'percent':
      return `${value}%`;
    case 'usd':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value);
    case 'decimal':
      return String(value);
    case 'integer':
      return String(value);
    default:
      return String(value);
  }
}

export function resolveDashboardSettingFromMap(
  map: Map<string, DashboardSettingDto>,
  name: string
): ResolvedDashboardSetting {
  const config = getFieldConfig(name);
  if (!config) {
    return {
      name,
      value: 0,
      defaultValue: 0,
      source: 'default',
      displayValue: '0',
    };
  }

  const defaultValue = parseDefaultNumber(config);
  const dto = map.get(name);
  const raw = dto?.value;

  if (dto && raw != null && raw.trim() !== '') {
    const parsed = parseRawValue(config, raw);
    if (isParsedValid(config.kind, raw, parsed)) {
      return {
        name,
        value: parsed,
        defaultValue,
        source: 'settings',
        displayValue: formatSettingDisplayValue(config, parsed),
      };
    }
  }

  return {
    name,
    value: defaultValue,
    defaultValue,
    source: 'default',
    displayValue: formatSettingDisplayValue(config, defaultValue),
  };
}

export function resolveDashboardSettingsFromMap(
  map: Map<string, DashboardSettingDto>,
  names: string[]
): Record<string, ResolvedDashboardSetting> {
  const out: Record<string, ResolvedDashboardSetting> = {};
  for (const name of names) {
    out[name] = resolveDashboardSettingFromMap(map, name);
  }
  return out;
}

/** Non-React helper for pure functions / mock data generation. */
export function getDefaultThresholdFromConfig(name: string): number {
  const config = getFieldConfig(name);
  if (!config) return 0;
  return parseDefaultNumber(config);
}
